export interface RenderMarkdownOptions {
  resolveHref(href: string): string;
}

export function renderMarkdown(markdown: string, options: RenderMarkdownOptions): string {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const html: string[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    if (trimmed.startsWith(':::cta')) {
      const title = trimmed.slice(':::cta'.length).trim();
      const contentLines: string[] = [];
      index += 1;
      while (index < lines.length && lines[index].trim() !== ':::') {
        contentLines.push(lines[index]);
        index += 1;
      }
      index += 1;
      html.push(renderCallout(title, contentLines, options));
      continue;
    }

    if (trimmed === ':::cards') {
      const cardLines: string[] = [];
      index += 1;
      while (index < lines.length && lines[index].trim() !== ':::') {
        if (lines[index].trim()) {
          cardLines.push(lines[index]);
        }
        index += 1;
      }
      index += 1;
      html.push(renderCards(cardLines, options));
      continue;
    }

    if (trimmed === ':::buttons') {
      const buttonLines: string[] = [];
      index += 1;
      while (index < lines.length && lines[index].trim() !== ':::') {
        if (lines[index].trim()) {
          buttonLines.push(lines[index]);
        }
        index += 1;
      }
      index += 1;
      html.push(renderButtons(buttonLines, options));
      continue;
    }

    if (trimmed.startsWith('```')) {
      const language = trimmed.slice(3).trim();
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith('```')) {
        codeLines.push(lines[index]);
        index += 1;
      }
      index += 1;
      html.push(`<pre class="doc-code"><code${language ? ` data-language="${escapeAttribute(language)}"` : ''}>${escapeHtml(codeLines.join('\n'))}</code></pre>`);
      continue;
    }

    const heading = /^(#{1,4})\s+(.+)$/.exec(trimmed);
    if (heading) {
      const level = Math.min(heading[1].length + 1, 5);
      html.push(`<h${level}>${renderInline(heading[2], options)}</h${level}>`);
      index += 1;
      continue;
    }

    if (isTableStart(lines, index)) {
      const tableLines: string[] = [];
      while (index < lines.length && lines[index].trim().startsWith('|')) {
        tableLines.push(lines[index]);
        index += 1;
      }
      html.push(renderTable(tableLines, options));
      continue;
    }

    if (/^-\s+/.test(trimmed)) {
      const items: string[] = [];
      while (index < lines.length && /^-\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^-\s+/, ''));
        index += 1;
      }
      html.push(`<ul>${items.map((item) => `<li>${renderInline(item, options)}</li>`).join('')}</ul>`);
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^\d+\.\s+/, ''));
        index += 1;
      }
      html.push(`<ol>${items.map((item) => `<li>${renderInline(item, options)}</li>`).join('')}</ol>`);
      continue;
    }

    const paragraphLines: string[] = [];
    while (index < lines.length && lines[index].trim() && !isBlockStart(lines, index)) {
      paragraphLines.push(lines[index].trim());
      index += 1;
    }
    if (paragraphLines.length === 0) {
      html.push(`<p>${renderInline(trimmed, options)}</p>`);
      index += 1;
      continue;
    }
    html.push(`<p>${renderInline(paragraphLines.join(' '), options)}</p>`);
  }

  return html.join('\n');
}

function renderCallout(title: string, contentLines: string[], options: RenderMarkdownOptions): string {
  const content = contentLines.filter((line) => line.trim()).map((line) => renderInline(line.trim(), options)).join(' ');
  return `
    <aside class="doc-callout">
      <strong>${renderInline(title, options)}</strong>
      <p>${content}</p>
    </aside>
  `;
}

function renderCards(cardLines: string[], options: RenderMarkdownOptions): string {
  const cards = cardLines.map((line) => {
    const [title = '', kicker = '', description = '', href = '', action = 'Open'] = line.split('|').map((part) => part.trim());
    return `
      <a class="doc-card" href="${escapeAttribute(options.resolveHref(href))}">
        <span class="doc-card-kicker">${escapeHtml(kicker)}</span>
        <strong>${escapeHtml(title)}</strong>
        <span>${renderInline(description, options)}</span>
        <em>${escapeHtml(action)}</em>
      </a>
    `;
  }).join('');

  return `<div class="doc-card-grid">${cards}</div>`;
}

function renderButtons(buttonLines: string[], options: RenderMarkdownOptions): string {
  const buttons = buttonLines.map((line) => {
    const [label = '', href = '', tone = 'secondary'] = line.split('|').map((part) => part.trim());
    const normalizedTone = tone.toLowerCase();
    const className = normalizedTone === 'primary'
      ? 'primary-link-button'
      : `secondary-link-button${normalizedTone === 'ghost' ? ' doc-button-ghost' : ''}`;
    return `<a class="${className}" href="${escapeAttribute(options.resolveHref(href))}">${escapeHtml(label)}</a>`;
  }).join('');

  return `<div class="doc-button-row">${buttons}</div>`;
}

function renderTable(tableLines: string[], options: RenderMarkdownOptions): string {
  const rows = tableLines
    .filter((line) => !/^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(line.trim()))
    .map(splitTableRow);

  const [headers = [], ...bodyRows] = rows;
  let inheritedFirstCell = '';
  const renderedBodyRows = bodyRows.map((row) => {
    const firstCell = row[0] ?? '';
    const isContinuation = firstCell.length === 0 && inheritedFirstCell.length > 0;
    if (firstCell.length > 0) {
      inheritedFirstCell = firstCell;
    }

    const cells = row.map((cell, cellIndex) => {
      if (isContinuation && cellIndex === 0) {
        return `<td class="doc-table-inherited-cell"><span class="sr-only">${renderInline(inheritedFirstCell, options)}</span></td>`;
      }
      return `<td>${renderInline(cell, options)}</td>`;
    }).join('');

    return `<tr${isContinuation ? ' class="doc-table-continuation"' : ''}>${cells}</tr>`;
  }).join('');

  return `
    <div class="doc-table-wrap">
      <table>
        <thead><tr>${headers.map((header) => `<th>${renderInline(header, options)}</th>`).join('')}</tr></thead>
        <tbody>${renderedBodyRows}</tbody>
      </table>
    </div>
  `;
}

function splitTableRow(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());
}

function isTableStart(lines: string[], index: number): boolean {
  return lines[index]?.trim().startsWith('|') && lines[index + 1]?.includes('---');
}

function isBlockStart(lines: string[], index: number): boolean {
  const trimmed = lines[index].trim();
  return trimmed.startsWith('#')
    || trimmed.startsWith(':::')
    || trimmed.startsWith('```')
    || trimmed.startsWith('|')
    || /^-\s+/.test(trimmed)
    || /^\d+\.\s+/.test(trimmed);
}

function renderInline(value: string, options: RenderMarkdownOptions): string {
  const placeholders: string[] = [];
  const withPlaceholders = value.replace(/\[([^\]]+)]\(([^)]+)\)/g, (_match, label: string, href: string) => {
    const placeholderIndex = placeholders.length;
    placeholders.push(`<a href="${escapeAttribute(options.resolveHref(href.trim()))}">${renderInlineText(label)}</a>`);
    return `\u0000${placeholderIndex}\u0000`;
  });

  return renderInlineText(withPlaceholders).replace(/\u0000(\d+)\u0000/g, (_match, placeholderIndex: string) => placeholders[Number(placeholderIndex)] ?? '');
}

function renderInlineText(value: string): string {
  return escapeHtml(value)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replace(/"/g, '&quot;');
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"]/g, (character) => {
    switch (character) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return character;
    }
  });
}
